import React, { useState } from 'react';
import { useSubscription } from '@apollo/react-hooks';
import { Table, TableBody, TableHead, TableRow, TableContainer, Paper, TableCell } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import { DeleteDialog } from '../util/DeleteDialog';
import { InfoDialog } from '../util/InfoDialog';
import { CellContent } from '../table/table';
import { capitalise } from '../../util/string';
import { isInfoOnlyEntity } from '../../util/consts';
import { getDependencyTree } from '../util/tree';
import { InlineProgress } from '../util/progress';

const DeletionComponent = ({
  entityType,
  records,
  fetchById,
  deleteEntityMutation,
  schema,
  parentType,
  parentId,
  refetchData,
  refetchCount,
}) => {
  // Check if this entity type should show info instead of delete
  const shouldShowInfo = isInfoOnlyEntity(entityType);

  const handleRefetchData = refetchData || (() => {});
  const handleRefetchCount = refetchCount || (() => {});

  let entityIds = [];
  records.forEach(record => {
    entityIds.push(record[`${entityType}_id`]);
  });

  const [dependencies, setDependencies] = useState({});
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [variables, setVariables] = useState({});

  const getDependencies = async (entityType, item) => {
    setDependencies(await getDependencyTree(`statsbomb.${entityType}`, item[`${entityType}_id`]));
    if (shouldShowInfo) {
      setInfoOpen(true);
    } else {
      setOpen(true);
    }
    setVariables({ [`${entityType}_id`]: item[`${entityType}_id`] });
  };

  const { loading, data } = useSubscription(fetchById, {
    variables: {
      entityIds,
    },
  });

  if (loading) return <InlineProgress />;
  const entityData = data?.[`statsbomb_golden_${entityType}`];

  if (!entityData || !Array.isArray(entityData)) {
    return (
      <div>
        <h1>
          {shouldShowInfo ? 'References for' : 'Delete'} {entityType}
        </h1>
        <h3>No data available for {entityType}</h3>
        <p>Data structure: {JSON.stringify(data, null, 2)}</p>
        <p>Expected key: statsbomb_golden_{entityType}</p>
        <p>Available keys: {data ? Object.keys(data).join(', ') : 'none'}</p>
      </div>
    );
  }

  return (
    <>
      <h1>
        {shouldShowInfo ? 'References for' : 'Delete'} {entityType}
      </h1>
      <h3>
        Listed down {entityData.length} {entityType}s related to {parentType} with {parentType} id : {parentId}
      </h3>
      {!shouldShowInfo && (
        <DeleteDialog
          modal={open}
          handleClose={() => setOpen(false)}
          dependencyStructure={dependencies}
          type={entityType}
          {...{
            open,
            deleteEntityMutation,
            variables, // Revert back to local variables state - it was working!
            refetchData: handleRefetchData,
            refetchCount: handleRefetchCount,
          }}
        />
      )}
      {shouldShowInfo && (
        <InfoDialog
          open={infoOpen}
          handleClose={() => setInfoOpen(false)}
          dependencyStructure={dependencies}
          entityId={variables[`${entityType}_id`]}
          type={entityType}
          refetchData={handleRefetchData}
          refetchCount={handleRefetchCount}
        />
      )}
      <TableContainer component={Paper}>
        <Table className="dialog-table" size="small" component={Paper}>
          <TableHead>
            <TableRow>
              {schema.map(item => (
                <TableCell style={{ fontSize: '12px', padding: 10, fontWeight: 'bold' }} key={item.columnKey}>
                  <span>{capitalise(item.columnKey)}</span>
                </TableCell>
              ))}
              <TableCell>{shouldShowInfo ? 'References' : 'Delete'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entityData?.map((item, index) => (
              <TableRow key={index}>
                {schema.map((item2, index) => (
                  <TableCell
                    onClick={() => window.open(`/${entityType}?${entityType}_id=${item[`${entityType}_id`]}`).focus()}
                    key={index}
                    style={{ fontSize: '10px', padding: 10 }}>
                    <CellContent
                      entity={item}
                      columnKey={item2.columnKey}
                      type={item2.type}
                      icon={item2.icon}
                      mappedIcon={item2.mapped_icon}
                      mappingFn={item2.mapping_function}></CellContent>
                  </TableCell>
                ))}
                <TableCell>
                  {shouldShowInfo ? (
                    <InfoIcon
                      className="info-button"
                      style={{ cursor: 'pointer' }}
                      onClick={() => getDependencies(entityType, item)}
                      title={`View references for this ${entityType}`}
                    />
                  ) : (
                    <DeleteIcon
                      className="delete-icon-button"
                      style={{ cursor: 'pointer' }}
                      onClick={() => getDependencies(entityType, item)}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DeletionComponent;
